import {
  Box,
  Flex,
  Grid,
  GridItem,
  HStack,
  Spinner,
  Text,
  VStack,
  Icon,
  Button,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel
} from "@chakra-ui/react";
import React, { useState, useRef, useEffect } from "react";
import { CommentaryContextProvider } from "../../context/CommentaryContext";
import TreeName from "../common/TreeName";
import {
  NodeInsights,
} from "../../containers/Interfaces";
import TreePanelContainer from "../../containers/analyse/TreePanelContainer";
import DataDisplayComponent from "./DataDisplayComponent";
import { epochConverter } from "../../utils/dateUtils";
import { Header } from "build-ts/metadata";
import { AnalysisTreeProto } from "build-ts/analysis";
import { FaTrash, FaDatabase, FaLightbulb } from "react-icons/fa";
import { useChat } from "../../context/ChatBotContext";
import InsightsPanelContainer from "containers/analyse/InsightsPanelContainer";
import { useUser } from "context/UserContext";

interface AnalysisPanelContainerProps {
  tree: AnalysisTreeProto;
  analysisName: string;
  analysisDescription: string;
  selectedNode: NodeInsights | null;
  nodeDataLoader: boolean;
  showTable: boolean;
  toggleView: () => void;
  handleAnalysisNameAndDescription: (name: string, description: string) => Promise<void>;
  isAdmin: boolean;
  treePannelLoader: boolean;
  setSelectedNodesToShowTables: React.Dispatch<React.SetStateAction<{node: NodeInsights, loading: boolean}[]>>;
  selectedNodesToShowTables: {node: NodeInsights, loading: boolean}[];
  isCommentaryVisible: boolean;
  setIsCommentaryVisible: React.Dispatch<React.SetStateAction<boolean>>;
  nodeInfoMap: Map<string, NodeInsights> | null;
  analysisInfo: Header | undefined;
  onToggleChat: () => void;
  isChatOpen: boolean;
  activeDataTables: Map<string, number>;
  handleClearAllTables: () => void;
  isClearAllTables: boolean;
  setIsClearAllTables: React.Dispatch<React.SetStateAction<boolean>>;
  handleSaveTree: () => Promise<void>;
}

const AnalysisPanel: React.FC<AnalysisPanelContainerProps> = ({
  tree,
  analysisName,
  analysisDescription,
  selectedNode,
  nodeDataLoader,
  handleAnalysisNameAndDescription,
  isAdmin,
  treePannelLoader,
  setSelectedNodesToShowTables,
  selectedNodesToShowTables,
  nodeInfoMap,
  analysisInfo,
  onToggleChat,
  isChatOpen,
  activeDataTables,
  handleClearAllTables,
  isClearAllTables,
  setIsClearAllTables,
  handleSaveTree
}) => {
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const { referencedNodeId } = useChat();
  const { defaultTimeZone } = useUser();
  const [tabIndex, setTabIndex] = useState(1); // Default to Insights tab
  const dataTablesRef = useRef<HTMLDivElement>(null);

  const handleNodeSelect = (node_id: string) => {
    setSelectedNodeId(node_id);
    // Switch to Data tab if currently on Insights tab
    if (tabIndex === 1) {
      setTabIndex(0);
    }
  };

  const handleTabChange = (index: number) => {
    setTabIndex(index);
  };

  //UseEffect to set the selected node id when a node is clicked from the chat so that Table is highlighted
  useEffect(() => {
    if (referencedNodeId?.nodeId) {
      setSelectedNodeId(referencedNodeId.nodeId);
    }
  }, [referencedNodeId]);


  return (
    <CommentaryContextProvider>
      <Box position="relative" height="100%">
        <Grid
          templateAreas="'tree insights'"
          templateColumns="35% 65%"
          templateRows="1fr"
          gap={0}
          height="100%"      
          p={0}
        >
          {/* Left panel - Tree */}
          <GridItem
            area={"tree"}
            boxShadow="md"
            bg="bg.surface"
            position="relative"
            borderRadius="lg"
            m={1}
            overflow="hidden"
          >
            <VStack w="100%" h="100%" spacing={0}>
              {/* Header - Fixed height */}
              <HStack
                justifyContent="space-between"
                p={0}
                spacing={2}
                textAlign="left"
                alignItems="left"
                width="100%"
                flexShrink={0}
              >
                <Box p={2} w="100%">
                  <Text fontWeight={600} color="gray.800" m={1} mb={0}>
                    <TreeName 
                      treeName={analysisName} 
                      treeDescription={analysisDescription}
                      handleTreeNameAndDescription={handleAnalysisNameAndDescription}
                      isChatOpen={isChatOpen}
                      handleSaveTree={handleSaveTree}
                    />
                  </Text>
                  <Text
                    fontSize="xs" 
                    color="gray.500"
                    m={1}
                    mt={0}
                  >
                    {analysisInfo?.created_date_ms && `(run on ${epochConverter(analysisInfo?.created_date_ms!)
                     || "Runtime info"}) ${defaultTimeZone}`}
                  </Text>
                </Box>
              </HStack>

              <Box px={3} w="100%" textAlign="left">
                  <Text 
                    display="inline-block"
                    px={2}
                    py={1}
                    borderRadius="md"
                    bg="purple.50"
                    color="gray.800"
                    fontSize="sm"
                    fontWeight={500}
                    textAlign="left"
                    mb={0}
                  >
                    Reasoning Tree
                  </Text>
              </Box>

              {/* Analysis Tree Viewer - takes remaining height */}
              <Box 
                flex="1" 
                width="100%" 
                overflow="auto"
                position="relative"
                minHeight={0}
              >
                <TreePanelContainer 
                  tree={tree} 
                  isChatOpen={isChatOpen} 
                  activeDataTables={activeDataTables}
                  onNodeSelect={handleNodeSelect}
                  isClearAllTables={isClearAllTables}
                  setIsClearAllTables={setIsClearAllTables}
                />
              </Box>
            </VStack>
          </GridItem>

          {/* Right panel - Data and Insights */}
          <GridItem
            area={"insights"}
            boxShadow="md"
            bg="bg.surface"
            position="relative"
            borderRadius="lg"
            m={1}
            overflow="hidden"
          >
            <Tabs 
              variant="soft-rounded" 
              colorScheme="purple" 
              isFitted 
              size="sm"
              height="100%"
              defaultIndex={1}
              index={tabIndex}
              onChange={handleTabChange}
             >
              <TabList>
                <Tab>
                  <Icon as={FaDatabase} boxSize={isChatOpen ? 3 : 4} color="gray.500" ml={2} mb={1} />
                  <Text fontSize={isChatOpen ? "sm" : "md"} fontWeight="600" color="gray.500" mt={0} textAlign="left" ml={2} mb={1}>DATA</Text>
                </Tab>
                <Tab>
                  <Icon as={FaLightbulb} boxSize={isChatOpen ? 3 : 4} color="gray.500" ml={2} mb={1} />
                  <Text fontSize={isChatOpen ? "sm" : "md"} fontWeight="600" color="gray.500" mt={0} textAlign="left" ml={2} mb={1}>INSIGHTS</Text>
                </Tab>
              </TabList>
                <TabPanels height="calc(100vh - 120px)">
                  <TabPanel p={0} height="100%">
                    {(nodeDataLoader || treePannelLoader) && (
                     <Box
                      p={2}
                      position="absolute"
                      top="10%"
                      right="40%"
                      zIndex="10"
                      bg="green.100"
                      borderRadius={3}
                     >
                      <Flex alignItems="center" justifyContent="center">
                        <HStack>
                          <Spinner size="sm" color="black" thickness="1px" />
                          <Text mb={0} fontSize="xs" fontWeight="semibold">
                            Fetching data...
                          </Text>
                         </HStack>
                       </Flex>
                     </Box>
                   )}
                   <Box
                    height="100%"
                    display="flex"
                    flexDirection="column"
                    width="100%"
                   >
                    <HStack justifyContent="flex-end" p={2}>
                     {/* Clear All Button */}
                     {selectedNodesToShowTables.length > 0 && (
                        <Button
                          leftIcon={<Icon as={FaTrash} boxSize={isChatOpen ? "4" : "4"} />}
                          aria-label="Clear all tables"
                          size={isChatOpen ? "xs" : "xs"}
                          colorScheme="red"
                          variant="ghost"
                          color="gray.500"
                          _hover={{ color: "red.500", borderColor: "red.500", bg: "gray.50" }}
                          onClick={handleClearAllTables}
                          borderColor="gray.300"
                          fontSize="xs"
                          iconSpacing={isChatOpen ? "0" : "1"}
                          mb={1}
                        >
                          {isChatOpen ? "" : "Clear"}
                        </Button>
                      )}
                    </HStack>
                    <Box 
                      flex="1" 
                      overflowY="auto"
                      p={1}
                      css={{
                        scrollbarWidth: "thin",
                        scrollbarColor: "#CBD5E0 transparent",
                        "&::-webkit-scrollbar": {
                          width: "4px",
                        },
                        "&::-webkit-scrollbar-track": {
                          background: "transparent",
                        },
                      }}
                    >
                      {selectedNodesToShowTables?.length === 0 && !nodeDataLoader ? (
                        <Text 
                          fontSize={isChatOpen ? "sm" : "md"} 
                          color="gray.500" 
                          p={isChatOpen ? 2 : 4} 
                          textAlign="center"
                        >
                          Select a node to view data
                        </Text>
                      ) : (
                        <VStack
                          spacing={1}
                          width="100%"
                          ref={dataTablesRef}
                        >
                        {selectedNodesToShowTables?.map((selected, index) => (
                          <Box
                            key={selected.node.current_node.id}
                            width="100%"
                            bg="bg.surface"
                            borderWidth={1}
                            borderColor="gray.300"
                            borderRadius="md"
                          >
                            <DataDisplayComponent
                              index={index}
                              tableNumber={(activeDataTables.get(selected.node.current_node.id!) ?? 0) + 1}                         
                              nodeDataLoader={selected.loading}
                              node={selected.node}
                              isAdmin={isAdmin}
                              isChatOpen={isChatOpen}
                              onClose={() => {
                                setSelectedNodesToShowTables((prev) =>
                                  prev.filter((n) => n.node.current_node.id !== selected.node.current_node.id)
                                );
                              }}
                              nodeInfoMap={nodeInfoMap}
                              selectedNodeId={selectedNodeId}
                            />
                          </Box>
                        ))}
                        </VStack>
                      )}
                    </Box>
                  </Box>
                 </TabPanel>
                 <TabPanel p={0} height="100%">
                   <Box
                     height="100%"
                     position="relative"
                     overflow="hidden"
                   >
                    {/* <CommentaryPanelContainer 
                      treeId={tree?.header?.id!} 
                      onToggleChat={onToggleChat}
                      isChatOpen={isChatOpen}
                      isExpanded={isInsightsExpanded}
                      onToggleExpand={() => setIsInsightsExpanded(!isInsightsExpanded)}
                    /> */}
                    <InsightsPanelContainer 
                      onToggleChat={onToggleChat}
                      isChatOpen={isChatOpen}
                    />
                  </Box>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </GridItem>
        </Grid>
        
        {/* Ask Anko button - Fixed position outside grid flow */}
        {!isChatOpen && (
          <Box
            position="absolute"
            bottom="10px"
            right="10px"
            zIndex={10}
          >
            <Button
              onClick={() => onToggleChat()}
              leftIcon={
                <Box
                  as="img"
                  src={require("../../assets/AnkoLogo256.png")}
                  alt="Anko Logo"
                  boxSize="21px"
                  transition="transform 0.6s linear"
                  animation="_groupHover={{ animation: 'spin 2s linear infinite' }}"
                  sx={{
                    "@keyframes spin": {
                      "0%": { transform: "rotate(0deg)" },
                      "100%": { transform: "rotate(360deg)" }
                    }
                  }}
                />
              }
              bg={`linear-gradient(135deg, 
                var(--chakra-colors-purple-50) 0%, 
                var(--chakra-colors-purple-100) 45%, 
                var(--chakra-colors-purple-200) 100%)`}
              color="purple.700"
              _hover={{ 
                bg: `linear-gradient(135deg, 
                  var(--chakra-colors-purple-50) 0%, 
                  var(--chakra-colors-purple-200) 45%,
                  var(--chakra-colors-purple-100) 100%)`,
                transform: "translateY(-1px)",
                shadow: "lg",
                "& img": { animation: "spin 2s linear infinite" },
                color: "purple.800"
              }}
              size="lg"
              fontWeight={600}
              borderRadius="lg"
              transition="all 0.2s ease"
              role="group"
              px={2}
              py={2}
              border="none"
              boxShadow="sm"
              iconSpacing={2}
            >
              Ask Anko
            </Button>
          </Box>
        )}
      </Box>
    </CommentaryContextProvider>
  );
};

export default AnalysisPanel;
